/**
 * 👷 LaborCalculator — Wave 3 · Employer Labor Calculator
 * ════════════════════════════════════════════════════════════════════════
 *
 * Public page at /labor. Interactive slider tool letting local business
 * partners estimate labor cost across three vetted tiers (General /
 * Skilled / Specialty). Live market comparison + lead capture into
 * `/api/v1/public/labor-leads`.
 *
 * SA-configurable market rates are pulled from `/api/v1/public/labor-config`
 * so the SA can retune rates from the admin Labor Rate Settings page
 * without a redeploy.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Briefcase, TrendingDown, Users, Clock, Calendar, Building2, Sparkles } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import useDocumentMeta from "../hooks/useDocumentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/site`;

const fmtUSD = (n) => "$" + Math.round(n || 0).toLocaleString();
const fmtHours = (n) => Math.round(n || 0).toLocaleString() + " hrs";

export default function LaborCalculator() {
  useDocumentMeta({
    title:       "Employer Labor Calculator | The Exchange Alliance",
    description: "Estimate your labor cost with vetted, program-supported workers. Interactive tool with live TEA vs. market comparison. Save 25-40% while investing in workforce reentry.",
    canonical:   "https://theexchangealliance.com/labor",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // Estimator state — sensible defaults
  const [tierId, setTierId] = useState("general");
  const [hoursPerWeek, setHoursPerWeek] = useState(20);
  const [workers, setWorkers] = useState(2);
  const [durationWeeks, setDurationWeeks] = useState(8);

  // Lead form state
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [form, setForm] = useState({
    business_name: "", contact_name: "", email: "", phone: "",
    start_date: "", project_scope: "", referrer: "",
  });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/v1/public/labor-config`, { withCredentials: false })
      .then((r) => {
        if (cancelled) return;
        setConfig(r.data);
        // Clamp state into config bounds
        setHoursPerWeek((h) => Math.min(Math.max(h, r.data.min_hours_per_week || 1), r.data.max_hours_per_week || 60));
        setWorkers((w) => Math.min(Math.max(w, r.data.min_workers || 1), r.data.max_workers || 25));
        setDurationWeeks((d) => Math.min(Math.max(d, r.data.min_duration_weeks || 1), r.data.max_duration_weeks || 52));
      })
      .catch(() => { if (!cancelled) toast.error("Couldn't load labor tiers. Please refresh."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const tier = useMemo(() => {
    if (!config) return null;
    return config.tiers.find((t) => t.id === tierId) || config.tiers[0];
  }, [config, tierId]);

  const estimate = useMemo(() => {
    if (!tier) return null;
    const totalHours = hoursPerWeek * workers * durationWeeks;
    const teaCost    = totalHours * (tier.tea_rate || 0);
    const marketCost = totalHours * (tier.market_rate || 0);
    const savings    = Math.max(0, marketCost - teaCost);
    const savingsPct = marketCost > 0 ? Math.round((savings / marketCost) * 100) : 0;
    return { totalHours, teaCost, marketCost, savings, savingsPct };
  }, [tier, hoursPerWeek, workers, durationWeeks]);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!estimate || !tier) return;
    if (form.business_name.trim().length < 1 || form.contact_name.trim().length < 1 || !form.email.trim()) {
      toast.error("Please fill in business name, your name, and email.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/v1/public/labor-leads`, {
        business_name: form.business_name.trim(),
        contact_name:  form.contact_name.trim(),
        email:         form.email.trim(),
        phone:         form.phone.trim() || null,
        tier_id:       tierId,
        hours_per_week: hoursPerWeek,
        workers:        workers,
        duration_weeks: durationWeeks,
        start_date:    form.start_date || null,
        project_scope: form.project_scope.trim() || null,
        referrer:      form.referrer.trim() || null,
      }, { withCredentials: false });
      setSubmitted(r.data);
      toast.success("Received — check your inbox for a confirmation.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Submission failed. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <main data-testid="labor-calculator-root" className="bg-[#FAFAF7] text-[#0A0612] font-sans">
      <Header />

      {/* ───── Hero ───── */}
      <section className="pt-28 pb-12 md:pt-36 md:pb-16 border-b border-[#0A0612]/5" data-testid="labor-hero">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C9A227]/50 bg-[#FFFBEC] text-[#0A0612] font-mono text-[10px] tracking-[0.22em] uppercase mb-6">
            <Sparkles className="h-3 w-3 text-[#C9A227]" />
            For Employers · Live Estimator
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6 max-w-4xl">
            Reliable labor, vetted through a reentry program — <span className="text-[#1B4332]">at 25–40% below market</span>.
          </h1>
          <p className="text-lg md:text-xl text-[#0A0612]/70 max-w-3xl leading-relaxed">
            Drag the sliders below to estimate what a labor engagement with The Exchange Alliance would look like for your business. Every worker we send has stable housing, active case management, and is being coached toward long-term employment — you get consistency AND social ROI.
          </p>
        </div>
      </section>

      {loading && (
        <section className="py-16 text-center text-[#0A0612]/60" data-testid="labor-loading">
          Loading calculator…
        </section>
      )}

      {config && !loading && (
        <>
          {/* ───── Tier Picker ───── */}
          <section className="py-10 border-b border-[#0A0612]/5" data-testid="labor-tier-picker">
            <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#1B4332] font-bold mb-4">
                1 · Pick a Labor Tier
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {config.tiers.map((t) => {
                  const active = t.id === tierId;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTierId(t.id)}
                      data-testid={`labor-tier-${t.id}`}
                      className={`text-left p-6 rounded-2xl border-2 transition-all hover:shadow-lg ${
                        active
                          ? "border-[#0A0612] bg-[#0A0612] text-white"
                          : "border-[#0A0612]/10 bg-white hover:border-[#C9A227]"
                      }`}
                    >
                      <div className={`font-mono text-[10px] tracking-[0.25em] uppercase mb-2 ${active ? "text-[#F4D88A]" : "text-[#C9A227]"}`}>
                        {t.label}
                      </div>
                      <div className="font-display text-2xl font-bold tracking-tight mb-1">
                        {fmtUSD(t.tea_rate)}<span className="text-base font-normal opacity-60">/hr</span>
                      </div>
                      <div className={`text-xs mb-3 line-through ${active ? "text-white/50" : "text-[#0A0612]/40"}`}>
                        market {fmtUSD(t.market_rate)}/hr
                      </div>
                      <div className={`text-sm leading-relaxed ${active ? "text-white/75" : "text-[#0A0612]/70"}`}>
                        {t.description}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          {/* ───── Sliders + Live Estimate ───── */}
          <section className="py-12 md:py-16 border-b border-[#0A0612]/5" data-testid="labor-estimator">
            <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24 grid grid-cols-1 lg:grid-cols-5 gap-10">
              {/* Sliders */}
              <div className="lg:col-span-3">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#1B4332] font-bold mb-6">
                  2 · Size the Engagement
                </div>

                <SliderRow
                  testid="slider-hours"
                  icon={<Clock className="h-4 w-4" />}
                  label="Hours per week (per worker)"
                  min={config.min_hours_per_week || 1}
                  max={config.max_hours_per_week || 60}
                  value={hoursPerWeek}
                  onChange={setHoursPerWeek}
                  unit="hrs"
                />
                <SliderRow
                  testid="slider-workers"
                  icon={<Users className="h-4 w-4" />}
                  label="Number of workers"
                  min={config.min_workers || 1}
                  max={config.max_workers || 25}
                  value={workers}
                  onChange={setWorkers}
                  unit={workers === 1 ? "worker" : "workers"}
                />
                <SliderRow
                  testid="slider-duration"
                  icon={<Calendar className="h-4 w-4" />}
                  label="Duration"
                  min={config.min_duration_weeks || 1}
                  max={config.max_duration_weeks || 52}
                  value={durationWeeks}
                  onChange={setDurationWeeks}
                  unit={durationWeeks === 1 ? "week" : "weeks"}
                />
              </div>

              {/* Live estimate card */}
              <div className="lg:col-span-2">
                <div className="rounded-3xl bg-gradient-to-br from-[#0A0612] to-[#1B4332] text-white p-8 md:p-10 shadow-2xl sticky top-24" data-testid="labor-estimate-card">
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4D88A]/80 mb-3">
                    Your Estimate
                  </div>
                  <div className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-none mb-2 tabular-nums" data-testid="labor-estimate-tea">
                    {fmtUSD(estimate?.teaCost || 0)}
                  </div>
                  <div className="text-white/60 text-sm mb-6">
                    Total for {fmtHours(estimate?.totalHours || 0)} at {fmtUSD(tier?.tea_rate || 0)}/hr
                  </div>

                  {estimate && estimate.savings > 0 && (
                    <div className="mt-4 pt-6 border-t border-white/10">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingDown className="h-4 w-4 text-emerald-400" />
                        <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-emerald-300">
                          Vs. Market Rate
                        </span>
                      </div>
                      <div className="text-white/50 line-through text-lg mb-1 tabular-nums" data-testid="labor-estimate-market">
                        {fmtUSD(estimate.marketCost)}
                      </div>
                      <div className="text-emerald-300 text-xl md:text-2xl font-bold tabular-nums" data-testid="labor-estimate-savings">
                        Save {fmtUSD(estimate.savings)}
                        <span className="text-sm font-normal text-emerald-400/80 ml-2">
                          ({estimate.savingsPct}%)
                        </span>
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setShowForm(true)}
                    data-testid="labor-cta-btn"
                    className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#F4D88A] text-[#0A0612] hover:bg-[#D4AF37] font-mono text-[11px] tracking-[0.18em] uppercase px-6 py-3.5 transition-colors border border-[#D4AF37]/60"
                  >
                    Request Formal Quote
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  <p className="mt-4 text-xs text-white/50 leading-relaxed">
                    Estimate only — final pricing confirmed after a brief scope conversation.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ───── Lead form ───── */}
          {showForm && !submitted && (
            <section className="py-12 md:py-16 border-b border-[#0A0612]/5 bg-white" data-testid="labor-lead-form">
              <div className="mx-auto max-w-[900px] px-6 md:px-12">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#1B4332] font-bold mb-4">
                  3 · Request a Formal Quote
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-8">
                  Tell us where to send it.
                </h2>
                <form onSubmit={submitLead} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Business Name *" required
                    value={form.business_name}
                    onChange={(v) => setForm({ ...form, business_name: v })}
                    testid="labor-form-business" />
                  <Field label="Your Name *" required
                    value={form.contact_name}
                    onChange={(v) => setForm({ ...form, contact_name: v })}
                    testid="labor-form-contact" />
                  <Field label="Work Email *" type="email" required
                    value={form.email}
                    onChange={(v) => setForm({ ...form, email: v })}
                    testid="labor-form-email" />
                  <Field label="Phone" type="tel"
                    value={form.phone}
                    onChange={(v) => setForm({ ...form, phone: v })}
                    testid="labor-form-phone" />
                  <Field label="Ideal Start Date" type="date"
                    value={form.start_date}
                    onChange={(v) => setForm({ ...form, start_date: v })}
                    testid="labor-form-start" />
                  <Field label="Referred by (optional)"
                    value={form.referrer}
                    onChange={(v) => setForm({ ...form, referrer: v })}
                    testid="labor-form-referrer" />
                  <div className="md:col-span-2">
                    <label className="block font-mono text-[9px] tracking-[0.25em] uppercase text-[#6B6F76] mb-2">
                      Project Scope / Details
                    </label>
                    <textarea
                      rows={4}
                      value={form.project_scope}
                      onChange={(e) => setForm({ ...form, project_scope: e.target.value })}
                      data-testid="labor-form-scope"
                      placeholder="Describe the work (e.g. warehouse pick/pack, event setup, kitchen prep). Add any certifications you need."
                      className="w-full rounded-none border border-[#0A0612]/15 bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#0A0612]"
                    />
                  </div>
                  <div className="md:col-span-2 pt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="labor-form-submit"
                      className="inline-flex items-center gap-2 bg-[#0A0612] text-[#F4D88A] hover:bg-[#1B4332] font-mono text-[11px] tracking-[0.18em] uppercase px-8 py-3.5 transition-colors disabled:opacity-60"
                    >
                      {submitting ? "Sending…" : "Send My Estimate"}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </form>
              </div>
            </section>
          )}

          {submitted && (
            <section className="py-16 md:py-20 border-b border-[#0A0612]/5 bg-white" data-testid="labor-confirmation">
              <div className="mx-auto max-w-[700px] px-6 md:px-12 text-center">
                <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500/10 text-emerald-600 grid place-items-center mb-6">
                  <Briefcase className="h-7 w-7" />
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4">
                  Received. We&apos;ll be in touch within one business day.
                </h2>
                <p className="text-[#0A0612]/70 leading-relaxed">
                  A confirmation just landed in your inbox with the estimate summary. Our workforce team will follow up shortly to walk through scope and timing.
                </p>
              </div>
            </section>
          )}

          {/* ───── Trust strip ───── */}
          <section className="py-12 border-b border-[#0A0612]/5" data-testid="labor-trust-strip">
            <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <TrustCard
                  icon={<Building2 className="h-5 w-5" />}
                  title="Stable housing"
                  body="Every worker lives in an Exchange Alliance sanctuary — reliability isn't optional." />
                <TrustCard
                  icon={<Users className="h-5 w-5" />}
                  title="Case-managed"
                  body="Weekly 1:1 sessions and life-skills coaching keep the workforce engaged." />
                <TrustCard
                  icon={<Briefcase className="h-5 w-5" />}
                  title="Employer of record"
                  body="TEA handles W-9, insurance, payroll, and dispute resolution. You get invoices, not HR headaches." />
              </div>
            </div>
          </section>
        </>
      )}

      <Footer />
    </main>
  );
}


function SliderRow({ testid, icon, label, min, max, value, onChange, unit }) {
  return (
    <div className="mb-8" data-testid={`${testid}-row`}>
      <div className="flex items-center justify-between mb-3">
        <label className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#0A0612]/70">
          <span className="text-[#C9A227]">{icon}</span>
          {label}
        </label>
        <span className="font-display text-2xl font-bold tabular-nums" data-testid={`${testid}-value`}>
          {value} <span className="text-sm font-normal text-[#0A0612]/50">{unit}</span>
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className="w-full h-2 bg-[#0A0612]/10 rounded-lg appearance-none cursor-pointer accent-[#1B4332]"
      />
      <div className="flex justify-between font-mono text-[9px] tracking-[0.18em] uppercase text-[#0A0612]/40 mt-1">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}


function Field({ label, value, onChange, type = "text", required = false, testid }) {
  return (
    <div>
      <label className="block font-mono text-[9px] tracking-[0.25em] uppercase text-[#6B6F76] mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="w-full rounded-none border border-[#0A0612]/15 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#0A0612]"
      />
    </div>
  );
}


function TrustCard({ icon, title, body }) {
  return (
    <div className="rounded-2xl border border-[#0A0612]/10 bg-white p-6">
      <div className="h-10 w-10 rounded-full bg-[#1B4332] text-[#F4D88A] grid place-items-center mb-4">
        {icon}
      </div>
      <div className="font-display text-xl font-bold tracking-tight mb-2">{title}</div>
      <div className="text-sm text-[#0A0612]/70 leading-relaxed">{body}</div>
    </div>
  );
}
